/*!
 * OutIn Fino product page — theme-editor safe interactions.
 * Reveal animations, metric odometer roll, sticky buy, UGC carousel,
 * FAQ accordion, lightbox gallery, variant swatches, quantity selector.
 * Single-load guarded, idempotent re-init, respects reduced-motion.
 *
 * Reveal system aligned with the Mino reveal system (assets/mino.js):
 * one IntersectionObserver per init, rootMargin -60px, reveal once,
 * reduced-motion / no-IO = show immediately, theme-editor aware.
 * Metric roll: observer arms the strip at 30% visibility, rolls once
 * per storefront page load, replays on editor section selection.
 */
(function () {
  if (window.__finoProductPageLoaded) return;
  window.__finoProductPageLoaded = true;

  var $  = function (sel, root) { return (root || document).querySelector(sel); };
  var $$ = function (sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); };
  var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ---------- Reveal on scroll (Mino system: IO-only, reveal once) ---------- */
  function initReveals(root) {
    var els = $$(".rv:not([data-fino-reveal-bound])", root);
    if (root && root.classList && root.classList.contains("rv") && !root.hasAttribute("data-fino-reveal-bound")) els.unshift(root);
    if (!els.length) return;
    if (reduce || !("IntersectionObserver" in window)) {
      els.forEach(function (el) {
        el.setAttribute("data-fino-reveal-bound", "1");
        el.classList.remove("is-hidden");
        el.classList.add("in");
      });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { en.target.classList.add("in"); io.unobserve(en.target); }
      });
    }, { rootMargin: "-60px" });
    els.forEach(function (el) {
      el.setAttribute("data-fino-reveal-bound", "1");
      el.classList.add("is-hidden");
      io.observe(el);
    });
  }

  /* Theme editor helper: complete pending reveals immediately (never leave
     a selected section/block hidden at zero opacity). */
  function revealNow(scope) {
    if (!scope || !scope.querySelectorAll) return;
    var list = $$(".rv", scope);
    if (scope.classList && scope.classList.contains("rv")) list.push(scope);
    if (scope.closest) {
      var up = scope.closest(".rv");
      if (up) list.push(up);
    }
    list.forEach(function (el) { el.classList.add("in"); });
  }

  /* ---------- Metric strip: odometer digit roll ---------- */
  /* Arm transitions first, then roll on the next frames */
  function playRoll(strip) {
    strip.classList.add("is-armed");
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { strip.classList.add("is-rolled"); });
    });
  }

  /* Jump straight to final values (editor block selection, no-IO fallback) */
  function completeRoll(strip) {
    strip.classList.add("fino-roll-instant", "is-armed", "is-rolled");
  }

  /* Shopify Customize: replay the roll when the section is selected */
  function replayRoll(strip) {
    if (strip.__finoRollIO) { try { strip.__finoRollIO.disconnect(); } catch (_) {} strip.__finoRollIO = null; }
    strip.classList.add("is-armed", "fino-roll-instant");
    strip.classList.remove("is-rolled");
    void strip.offsetWidth; // reflow so tracks reset instantly
    strip.classList.remove("fino-roll-instant");
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { strip.classList.add("is-rolled"); });
    });
  }

  function initMetricRoll(root) {
    var strips = $$("[data-fino-metric-strip]:not([data-fino-roll-bound])", root);
    if (root && root.matches && root.matches("[data-fino-metric-strip]") && !root.hasAttribute("data-fino-roll-bound")) strips.unshift(root);
    if (!strips.length) return;
    strips.forEach(function (strip) {
      strip.setAttribute("data-fino-roll-bound", "1");
      if (reduce) return; // reduced motion: keep the server-rendered final values
      var values = $$("[data-fino-metric-value]", strip);
      if (!values.length) return;
      var duration = parseInt(strip.getAttribute("data-roll-duration"), 10) || 900;
      var stagger = parseInt(strip.getAttribute("data-roll-stagger"), 10);
      if (isNaN(stagger)) stagger = 120;

      values.forEach(function (el, mi) {
        if (el.__finoOdo || el.querySelector(".fino-odo")) return; // never duplicate digit markup
        el.__finoOdo = true;
        var text = el.textContent.replace(/\s+/g, " ").trim();
        if (!text) return;
        var delay = mi * stagger;
        var frag = document.createDocumentFragment();

        // Static copy for assistive technology
        var sr = document.createElement("span");
        sr.className = "fino-odo-sr";
        sr.textContent = text;
        frag.appendChild(sr);

        // Animated, aria-hidden display
        var anim = document.createElement("span");
        anim.className = "fino-odo";
        anim.setAttribute("aria-hidden", "true");

        function makeTrack(steps) {
          var tr = document.createElement("span");
          tr.className = "fino-odo-track";
          tr.style.transitionDuration = duration + "ms";
          tr.style.transitionDelay = delay + "ms";
          tr.style.setProperty("--odo-steps", steps);
          return tr;
        }

        if (!/[0-9]/.test(text)) {
          // No digits at all: graceful vertical reveal of the whole value
          var vp = document.createElement("span");
          vp.className = "fino-odo-digit";
          var tr = makeTrack(1);
          var pad = document.createElement("span"); pad.className = "fino-odo-cell"; pad.innerHTML = "&nbsp;";
          var fin = document.createElement("span"); fin.className = "fino-odo-cell"; fin.textContent = text;
          tr.appendChild(pad); tr.appendChild(fin);
          vp.appendChild(tr);
          anim.appendChild(vp);
        } else {
          for (var i = 0; i < text.length; i++) {
            var ch = text.charAt(i);
            if (ch >= "0" && ch <= "9") {
              var d = Number(ch);
              var steps = d === 0 ? 10 : d; // zeros roll a full cycle
              var dvp = document.createElement("span");
              dvp.className = "fino-odo-digit";
              var dtr = makeTrack(steps);
              for (var s = 0; s <= steps; s++) {
                var cell = document.createElement("span");
                cell.className = "fino-odo-cell";
                cell.textContent = String(s % 10);
                dtr.appendChild(cell);
              }
              dvp.appendChild(dtr);
              anim.appendChild(dvp);
            } else {
              // Suffixes / prefixes / separators stay attached and static
              var st = document.createElement("span");
              st.className = "fino-odo-static";
              st.textContent = ch;
              anim.appendChild(st);
            }
          }
        }

        frag.appendChild(anim);
        el.textContent = "";
        el.appendChild(frag);
      });

      if (!("IntersectionObserver" in window)) { completeRoll(strip); return; }
      // Start only when ~30% of the strip is visible; once per page load
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting && en.intersectionRatio >= 0.3) {
            io.disconnect();
            strip.__finoRollIO = null;
            playRoll(strip);
          }
        });
      }, { threshold: [0.3] });
      io.observe(strip);
      strip.__finoRollIO = io;
    });
  }

  /* ---------- FAQ accordion ---------- */
  function initFAQ(root) {
    $$(".fino-faq-item", root).forEach(function (item) {
      if (item.__finoFaqBound) return;
      item.__finoFaqBound = true;
      var btn = item.querySelector("button"); var ans = item.querySelector(".faq-answer");
      if (!btn || !ans) return;
      btn.setAttribute("aria-expanded", item.classList.contains("open") ? "true" : "false");
      btn.addEventListener("click", function () {
        var open = item.classList.toggle("open");
        btn.setAttribute("aria-expanded", open ? "true" : "false");
        ans.hidden = !open;
      });
      ans.hidden = !item.classList.contains("open");
    });
  }

  /* ---------- Sticky buy bar ---------- */
  function initSticky(root) {
    var buy = root.querySelector("[data-fino-purchase]");
    var bar = document.querySelector("[data-fino-sticky-buy]");
    if (!buy || !bar) return;
    var check = function () {
      var r = buy.getBoundingClientRect();
      var show = r.bottom < 80 && (window.scrollY || 0) > 520;
      bar.classList.toggle("is-visible", show);
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check, { passive: true });
  }

  /* ---------- UGC carousel ---------- */
  function initUGC(root) {
    var rail = root.querySelector("[data-fino-ugc-rail]");
    if (!rail || rail.__finoBound) return;
    rail.__finoBound = true;
    var prev = root.querySelector("[data-fino-ugc-prev]");
    var next = root.querySelector("[data-fino-ugc-next]");
    var scroll = function (dir) {
      rail.scrollBy({ left: dir * Math.max(260, rail.clientWidth * 0.8), behavior: reduce ? "auto" : "smooth" });
    };
    if (prev) prev.addEventListener("click", function () { scroll(-1); });
    if (next) next.addEventListener("click", function () { scroll(1); });

    var videos = $$(".ugc-card video", rail);
    videos.forEach(function (v, i) {
      v.muted = true; v.playsInline = true; v.preload = "metadata";
      var card = v.closest(".ugc-card");
      var playBtn = card.querySelector(".ugc-play");
      var soundBtn = card.querySelector(".ugc-sound");
      var setPlaying = function (on) {
        if (playBtn) playBtn.classList.toggle("is-playing", on);
      };
      v.addEventListener("play", function () { setPlaying(true); });
      v.addEventListener("pause", function () { setPlaying(false); });
      v.addEventListener("ended", function () { setPlaying(false); v.currentTime = 0; });
      var toggle = function (e) {
        if (e) e.preventDefault();
        videos.forEach(function (o) { if (o !== v) { o.pause(); } });
        if (v.paused) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
        else v.pause();
      };
      if (playBtn) playBtn.addEventListener("click", toggle);
      v.addEventListener("click", toggle);
      if (soundBtn) soundBtn.addEventListener("click", function (e) {
        e.stopPropagation();
        var unmute = v.muted;
        videos.forEach(function (o, j) { o.muted = j !== i || !unmute; });
        soundBtn.classList.toggle("is-unmuted", unmute);
      });
    });
  }

  /* ---------- Video hero autoplay (muted) ---------- */
  function initHeroVideos(root) {
    $$("[data-fino-autoplay]", root).forEach(function (v) {
      if (v.__finoAuto) return;
      v.__finoAuto = true;
      v.muted = true; v.loop = true; v.playsInline = true;
      var frame = v.closest(".video-hero, .video-divider");
      var btn = frame ? frame.querySelector("[data-fino-video-toggle]") : null;
      function sync() {
        if (!btn) return;
        var paused = v.paused;
        btn.setAttribute("aria-pressed", paused ? "true" : "false");
        btn.setAttribute("aria-label", (paused ? "Play " : "Pause ") + (v.getAttribute("aria-label") || "video"));
        var playIcon = btn.querySelector("[data-fino-video-play]");
        var pauseIcon = btn.querySelector("[data-fino-video-pause]");
        if (playIcon) playIcon.style.display = paused ? "" : "none";
        if (pauseIcon) pauseIcon.style.display = paused ? "none" : "";
      }
      if (btn && !btn.__finoVideoBound) {
        btn.__finoVideoBound = true;
        btn.addEventListener("click", function () {
          if (v.paused) {
            var playPromise = v.play();
            if (playPromise && playPromise.catch) playPromise.catch(sync);
          } else {
            v.pause();
          }
          sync();
        });
      }
      v.addEventListener("play", sync);
      v.addEventListener("pause", sync);
      var src = v.currentSrc || v.getAttribute("src");
      if (!src) { sync(); return; }
      if (reduce) {
        v.removeAttribute("autoplay");
        v.pause();
        sync();
        return;
      }
      var p = v.play();
      if (p && p.catch) p.catch(sync);
      sync();
    });
  }

  /* ---------- Product main: variants, media, quantity, lightbox ---------- */
  function initMainProduct(root) {
    var section = root.querySelector("[data-fino-product]");
    if (!section || section.__finoBound) return;
    section.__finoBound = true;

    // Variant data
    var dataEl = section.querySelector("[data-fino-variants]");
    var variants = [];
    try { variants = dataEl ? JSON.parse(dataEl.textContent) : []; } catch (e) {}

    var main    = section.querySelector("[data-fino-gallery-main-img]");
    var tiles   = $$("[data-fino-tile]", section);
    var swatches= $$("[data-fino-swatch]", section);
    var priceEl = section.querySelector("[data-fino-price]");
    var compareEl = section.querySelector("[data-fino-compare]");
    var savingEl = section.querySelector("[data-fino-save]");
    var availEl = section.querySelector("[data-fino-avail]");
    var availDot = section.querySelector(".fino-avail__dot");
    var addBtn  = section.querySelector("[data-fino-add]");
    var buyBtn  = section.querySelector("[data-fino-buy]");
    var qtyInput= section.querySelector("[data-fino-qty]");
    var qtyDec  = section.querySelector("[data-fino-qty-dec]");
    var qtyInc  = section.querySelector("[data-fino-qty-inc]");
    var variantIdInput = section.querySelector("[data-fino-variant-id]");
    var colorLabel = section.querySelector("[data-fino-color-label]");
    var stickyImg = document.querySelector("[data-fino-sticky-img]");
    var stickyName = document.querySelector("[data-fino-sticky-name]");
    var stickyPrice = document.querySelector("[data-fino-sticky-price]");

    // Gallery images (per variant if provided, else product images)
    var galleryImagesEl = section.querySelector("[data-fino-gallery-images]");
    var galleryImages = [];
    try { galleryImages = galleryImagesEl ? JSON.parse(galleryImagesEl.textContent) : []; } catch (e) {}
    var baseGalleryImages = galleryImages.slice();

    function setVariantGalleryImage(url) {
      if (!url) return;
      galleryImages = [url].concat(baseGalleryImages.filter(function (candidate) { return candidate !== url; }));
      var firstTileImage = section.querySelector("[data-fino-tile] .gallery-tile-img");
      if (firstTileImage) firstTileImage.style.backgroundImage = "url(" + JSON.stringify(url) + ")";
      if (main) main.src = url;
      if (stickyImg) stickyImg.src = url;
      markThumb(url);
    }

    function fmtMoney(cents, currency) {
      // cents is Shopify integer (price already in cents in Liquid |money uses store format); here we get cents
      currency = currency || (window.Shopify && Shopify.currency && Shopify.currency.active) || "MYR";
      var val = cents / 100;
      try { return new Intl.NumberFormat("en-MY", { style: "currency", currency: currency, maximumFractionDigits: 0 }).format(val); }
      catch (e) { return currency + " " + val.toFixed(0); }
    }

    function selectVariant(id) {
      var v = null;
      for (var i = 0; i < variants.length; i++) { if (String(variants[i].id) === String(id)) { v = variants[i]; break; } }
      if (!v) return;
      if (variantIdInput) variantIdInput.value = v.id;
      if (priceEl) priceEl.textContent = fmtMoney(v.price);
      if (compareEl) {
        if (v.compare_at_price && v.compare_at_price > v.price) {
          compareEl.textContent = fmtMoney(v.compare_at_price);
          compareEl.hidden = false;
          if (savingEl) {
            savingEl.textContent = "Save " + fmtMoney(v.compare_at_price - v.price);
            savingEl.hidden = false;
          }
        } else {
          compareEl.hidden = true;
          if (savingEl) savingEl.hidden = true;
        }
      }
      if (availEl) availEl.textContent = v.available ? (window.__finoInStockLabel || "In stock — ready to ship") : (window.__finoSoldOutLabel || "Sold out");
      if (availDot) availDot.classList.toggle("fino-avail__dot--out", !v.available);
      if (addBtn) {
        addBtn.disabled = !v.available;
        addBtn.textContent = v.available ? (addBtn.getAttribute("data-label-available") || "Add to Cart") : (addBtn.getAttribute("data-label-sold-out") || "Sold out");
      }
      if (buyBtn) buyBtn.disabled = !v.available;
      if (colorLabel) colorLabel.textContent = v.title || "";
      if (stickyName) stickyName.textContent = v.title || "";
      if (stickyPrice) stickyPrice.textContent = fmtMoney(v.price);

      // Media swap: make the selected variant image the featured gallery media
      var img = v.featured_image;
      setVariantGalleryImage(img);
      // update URL param ?variant=
      if (history && history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set("variant", v.id);
        history.replaceState({}, "", url.toString());
      }
      // Highlight active swatch
      var checkIcon = section.querySelector("[data-fino-swatch] svg");
      swatches.forEach(function (b) {
        var active = String(b.getAttribute("data-variant-id")) === String(v.id);
        b.setAttribute("aria-checked", active ? "true" : "false");
        b.classList.toggle("active", active);
        if (active && checkIcon && !b.contains(checkIcon)) b.appendChild(checkIcon);
      });
    }

    swatches.forEach(function (b) {
      b.addEventListener("click", function () { selectVariant(b.getAttribute("data-variant-id")); });
    });

    // Quantity
    if (qtyDec) qtyDec.addEventListener("click", function () { if (!qtyInput) return; var n = Math.max(1, (parseInt(qtyInput.value, 10) || 1) - 1); qtyInput.value = n; });
    if (qtyInc) qtyInc.addEventListener("click", function () { if (!qtyInput) return; var n = (parseInt(qtyInput.value, 10) || 1) + 1; qtyInput.value = n; });

    // Buy now
    if (buyBtn) buyBtn.addEventListener("click", function (e) {
      e.preventDefault();
      var vid = variantIdInput ? variantIdInput.value : "";
      var q = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
      if (!vid || buyBtn.getAttribute("aria-busy") === "true") return;
      var errorEl = section.querySelector("[data-fino-buy-error]");
      if (!errorEl) {
        errorEl = document.createElement("p");
        errorEl.setAttribute("data-fino-buy-error", "");
        errorEl.setAttribute("role", "alert");
        errorEl.style.cssText = "margin:.75rem 0 0;color:#f7b4a9;font-size:.85rem;font-weight:700;";
        var buttons = buyBtn.closest(".fino-buybtns");
        if (buttons) buttons.insertAdjacentElement("afterend", errorEl);
      }
      errorEl.textContent = "";
      buyBtn.setAttribute("aria-busy", "true");
      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ items: [{ id: Number(vid), quantity: q }] })
      }).then(function (response) {
        return response.json().catch(function () { return {}; }).then(function (data) {
          if (!response.ok) throw new Error(data.description || data.message || "Unable to add this item.");
          return data;
        });
      }).then(function () {
        window.location.href = "/checkout";
      }).catch(function (error) {
        errorEl.textContent = error.message || "Unable to add this item. Please try again.";
      }).finally(function () {
        buyBtn.removeAttribute("aria-busy");
      });
    });

    // Legacy tile layout: tile click -> open lightbox
    tiles.forEach(function (t, idx) {
      t.addEventListener("click", function (e) { e.preventDefault(); openLightbox(idx); });
    });

    // Mino-style gallery: thumbnails swap the main image; the main image opens the lightbox
    var mainBtn = section.querySelector("[data-fino-gallery-main]");
    var thumbs = $$("[data-fino-thumb]", section);
    var swiped = false;
    function sameImg(a, b) { return !!a && !!b && a.split("?")[0] === b.split("?")[0]; }
    function markThumb(src) {
      thumbs.forEach(function (t) {
        t.setAttribute("aria-current", sameImg(t.getAttribute("data-media-src"), src) ? "true" : "false");
      });
    }
    thumbs.forEach(function (t) {
      t.addEventListener("click", function () {
        if (t.querySelector("[data-fino-more]")) { openLightbox(Number(t.getAttribute("data-index")) || 0); return; }
        var src = t.getAttribute("data-media-src");
        if (main && src) main.src = src;
        markThumb(src);
      });
    });
    if (mainBtn) {
      mainBtn.addEventListener("click", function (e) {
        e.preventDefault();
        if (swiped) { swiped = false; return; }
        var idx = 0;
        if (main) {
          for (var gi = 0; gi < galleryImages.length; gi++) {
            if (sameImg(galleryImages[gi], main.src)) { idx = gi; break; }
          }
        }
        openLightbox(idx);
      });
      // Mino gallery swipe: advance through thumbnails
      var x0 = null;
      mainBtn.addEventListener("pointerdown", function (e) { x0 = e.clientX; });
      mainBtn.addEventListener("pointerup", function (e) {
        if (x0 === null) return;
        var dx = e.clientX - x0; x0 = null;
        if (Math.abs(dx) < 50 || !thumbs.length) return;
        swiped = true;
        var curIdx = -1;
        thumbs.forEach(function (t, ti) { if (t.getAttribute("aria-current") === "true") curIdx = ti; });
        var nxt = (Math.max(0, curIdx) + (dx < 0 ? 1 : -1) + thumbs.length) % thumbs.length;
        var nt = thumbs[nxt];
        if (nt.querySelector("[data-fino-more]")) nt = thumbs[0];
        var src = nt.getAttribute("data-media-src");
        if (main && src) main.src = src;
        markThumb(src);
      });
    }

    /* Lightbox */
    var lb = null;
    function buildLightbox() {
      if (lb) return lb;
      lb = document.createElement("div");
      lb.className = "fino-page gallery-modal-wrap";
      lb.setAttribute("role", "dialog");
      lb.setAttribute("aria-modal", "true");
      lb.setAttribute("aria-label", "OutIn Fino product gallery");
      lb.innerHTML =
        '<button class="gallery-modal-backdrop" data-fino-lb-close aria-label="Close product gallery"></button>' +
        '<div class="gallery-modal">' +
          '<button class="gallery-modal-close icon-button" data-fino-lb-close aria-label="Close gallery">✕</button>' +
          '<button class="gallery-modal-arrow left" data-fino-lb-nav="-1" aria-label="Previous image">‹</button>' +
          '<img class="gallery-modal-image" data-fino-lb-img alt="">' +
          '<button class="gallery-modal-arrow right" data-fino-lb-nav="1" aria-label="Next image">›</button>' +
          '<div class="gallery-modal-thumbs" data-fino-lb-thumbs></div>' +
        '</div>';
      lb.style.cssText = "position:fixed;inset:0;z-index:9000;display:none;";
      document.body.appendChild(lb);
      lb.addEventListener("click", function (e) {
        if (e.target.matches("[data-fino-lb-close]")) close();
        var n = e.target.closest("[data-fino-lb-nav]");
        if (n) { nav(Number(n.getAttribute("data-fino-lb-nav"))); return; }
        var t = e.target.closest("[data-fino-lb-thumb]");
        if (t) { show(Number(t.getAttribute("data-index"))); return; }
      });
      document.addEventListener("keydown", function (e) {
        if (!lb || lb.style.display !== "flex") return;
        if (e.key === "Escape") close();
        else if (e.key === "ArrowRight") nav(1);
        else if (e.key === "ArrowLeft") nav(-1);
      });
      return lb;
    }
    var current = 0;
    function show(i) {
      if (!galleryImages.length) return;
      current = (i + galleryImages.length) % galleryImages.length;
      var img = lb.querySelector("[data-fino-lb-img]");
      img.src = galleryImages[current];
      var rail = lb.querySelector("[data-fino-lb-thumbs]");
      rail.innerHTML = galleryImages.map(function (u, idx) {
        return '<button class="' + (idx === current ? "active" : "") + '" data-fino-lb-thumb data-index="' + idx + '" aria-label="View image ' + (idx + 1) + '"><img src="' + u + '" alt=""></button>';
      }).join("");
    }
    function openLightbox(i) {
      if (!galleryImages.length) return;
      buildLightbox();
      lb.style.display = "flex";
      lb.style.flexDirection = "column";
      document.body.style.overflow = "hidden";
      show(i || 0);
    }
    function close() {
      if (!lb) return;
      lb.style.display = "none";
      document.body.style.overflow = "";
    }
    function nav(d) { show(current + d); }

    // Initial: check ?variant= URL and select
    var url = new URL(window.location.href);
    var initial = url.searchParams.get("variant");
    var initialId = initial || (variantIdInput ? variantIdInput.value : "");
    if (initialId) selectVariant(initialId);
  }

  /* ---------- Boot ---------- */
  function init(root) {
    root = root || document;
    initReveals(root);
    initMetricRoll(root);
    initFAQ(root);
    initSticky(root);
    initUGC(root);
    initHeroVideos(root);
    initMainProduct(root);
  }
  function ready(fn) { if (document.readyState !== "loading") fn(); else document.addEventListener("DOMContentLoaded", fn); }
  ready(function () { init(document); });

  /* Theme-editor lifecycle */
  document.addEventListener("shopify:section:load", function (e) { init(e.target); });
  document.addEventListener("shopify:section:unload", function (e) {
    // clean up bound flags and observers so re-init works
    $$("[data-fino-product], [data-fino-ugc-rail], .fino-faq-item", e.target).forEach(function (n) {
      try { delete n.__finoBound; delete n.__finoFaqBound; delete n.__finoAuto; } catch (_) {}
    });
    $$("[data-fino-metric-strip]", e.target).forEach(function (s) {
      try { if (s.__finoRollIO) s.__finoRollIO.disconnect(); } catch (_) {}
    });
  });
  document.addEventListener("shopify:section:select", function (e) {
    // Selected sections must be visible immediately — complete pending reveals
    revealNow(e.target);
    // Metric strips replay their roll for the merchant preview
    $$("[data-fino-metric-strip]", e.target).forEach(replayRoll);
    // If the selected section contains a video hero, pause background autoplay for merchant control
    $$("[data-fino-autoplay]", e.target).forEach(function (v) { try { v.pause(); } catch (_) {} });
  });
  document.addEventListener("shopify:block:select", function (e) {
    // Selected blocks must never stay at zero opacity or mid-roll
    revealNow(e.target);
    var strip = e.target.closest ? e.target.closest("[data-fino-metric-strip]") : null;
    if (strip) completeRoll(strip);
    e.target.scrollIntoView({ block: "center", behavior: "smooth" });
  });
})();